import { useCanWrite } from '@hooks/useCanWrite';
import React, { useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useTaskTemplates, useDeleteTaskTemplate } from '@hooks/api/taskTemplates';
import { TaskTemplateCard } from '@components/molecules/TaskTemplateCard';
import { EmptyData } from '@components/molecules/EmptyData';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';
import { TemplateDetailModal } from '@components/organisms/TemplateDetailModal';
import { Input } from '@components/atoms/Input';
import { Loading } from '@components/atoms/Loading';
import { Button } from '@components/atoms/Button';
import { TaskTemplate } from '@models/taskTemplate.model';
import { Search, Sparkles } from 'lucide-react';
import { FileCopyOutlined } from '@mui/icons-material';
import { useAppSelector } from '@redux/hooks';
import { roleRank, TEMPLATE_ACTIONS_MIN_RANK } from '@constants/roleHierarchy';
import { useAiPlanningAvailable } from '@hooks/api/projectPlanDrafts';
import { useTranslation } from '@hooks/useTranslation';

const TemplatesPage: React.FC = () => {
  // Covers both causes of a locked account: a read-only membership, and an organization
  // whose trial has expired.
  const { canWrite } = useCanWrite();
  const isReadOnly = !canWrite;
  const role = useAppSelector((state) => state.user.role);
  const history = useHistory();
  const { data: aiPlanningAvailable } = useAiPlanningAvailable();
  const t = useTranslation('templatesPage');
  const canGenerateWithAi = roleRank(role) >= TEMPLATE_ACTIONS_MIN_RANK && aiPlanningAvailable;

  const [searchQuery, setSearchQuery] = useState('');
  const [templateToDelete, setTemplateToDelete] = useState<TaskTemplate | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<TaskTemplate | null>(null);

  const { data: templates, isLoading, error } = useTaskTemplates();
  const deleteTemplateMutation = useDeleteTaskTemplate();

  const filteredTemplates = useMemo(() => {
    if (!templates) return [];
    if (!searchQuery.trim()) return templates;

    const query = searchQuery.toLowerCase();
    return templates.filter(
      (template) =>
        template.name.toLowerCase().includes(query) ||
        template.description?.toLowerCase().includes(query)
    );
  }, [templates, searchQuery]);

  const handleDeleteClick = (template: TaskTemplate) => {
    setTemplateToDelete(template);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;

    await deleteTemplateMutation.mutateAsync(templateToDelete.templateId);
    setIsDeleteModalOpen(false);
    setTemplateToDelete(null);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setTemplateToDelete(null);
  };

  const handleCardClick = (template: TaskTemplate) => {
    setSelectedTemplate(template);
  };

  const handleCloseDetailModal = () => {
    setSelectedTemplate(null);
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="text-red-500">Error loading templates. Please try again.</div>
      </div>
    );
  }

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-2">
          <FileCopyOutlined
            sx={{ fontSize: 28 }}
            className="text-orange-400 dark:text-orange-300"
          />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Templates
          </h1>
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto">
          {canGenerateWithAi && (
            <Button
              variant="secondary"
              onClick={() => history.push('/templates/generate')}
              leftIcon={<Sparkles className="w-4 h-4" />}
              data-testid="templates-generate-with-ai">
              {t.generateWithAi}
            </Button>
          )}
          {/* Search */}
          <div className="w-full sm:w-72">
            <Input
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              iconLeft={<Search className="w-4 h-4 text-gray-400" />}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <Loading />
        </div>
      ) : filteredTemplates.length === 0 ? (
        <EmptyData
          description={
            searchQuery
              ? 'No templates match your search'
              : 'No templates yet. Create one from a project.'
          }
          fullHeight
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredTemplates.map((template) => (
            <TaskTemplateCard
              key={template.templateId}
              template={template}
              onClick={() => handleCardClick(template)}
              onDelete={
                isReadOnly || roleRank(role) < TEMPLATE_ACTIONS_MIN_RANK
                  ? undefined
                  : () => handleDeleteClick(template)
              }
            />
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        title="Delete Template"
        description={`Are you sure you want to delete "${templateToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      {/* Template Detail Modal */}
      <TemplateDetailModal
        isOpen={!!selectedTemplate}
        onClose={handleCloseDetailModal}
        template={selectedTemplate}
      />
    </div>
  );
};

export default TemplatesPage;
